import { Fragment, useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  admin,
  type AdminEmailRow,
  type AdminEmailStatus,
  type AdminEmailThrottle,
} from '../../api/admin'
import { ApiError } from '../../api/client'

const STATUSES: AdminEmailStatus[] = ['queued', 'sent', 'failed']
const PAGE_SIZE = 50

/**
 * /admin/emails — la cola de correo del producto (`email_outbox`).
 *
 * Todo el mail transaccional (alta de jugador, invitaciones a cuestionarios,
 * staff invites, secuencias) sale por acá, y desde 2026-09-08 sale DE VERDAD a
 * los destinatarios reales, con un FRENO GLOBAL: como máximo un mail cada
 * `EMAIL_MIN_INTERVAL_S` (30 min en prod), el resto espera. Por eso la
 * pantalla no alcanza con listar: tiene que decir a qué hora sale cada
 * pendiente, y dejar saltear el freno para un mail puntual.
 */
export function AdminEmails() {
  const [params, setParams] = useSearchParams()
  const status = (params.get('status') ?? '') as AdminEmailStatus | ''
  const kind = params.get('kind') ?? ''
  const offset = Number(params.get('offset') ?? '0') || 0

  const [items, setItems] = useState<AdminEmailRow[] | null>(null)
  const [total, setTotal] = useState(0)
  const [throttle, setThrottle] = useState<AdminEmailThrottle | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [sendingId, setSendingId] = useState<number | null>(null)

  const load = useCallback(() => {
    setError(null)
    return admin
      .emails({
        status: status || undefined,
        kind: kind || undefined,
        limit: PAGE_SIZE,
        offset,
      })
      .then((r) => {
        setItems(r.items)
        setTotal(r.total)
        setThrottle(r.throttle)
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Error de red.'))
  }, [status, kind, offset])

  useEffect(() => {
    void load()
  }, [load])

  function setParam(name: string, value: string) {
    const next = new URLSearchParams(params)
    if (value) next.set(name, value)
    else next.delete(name)
    next.delete('offset')
    setParams(next)
  }

  function setOffset(next: number) {
    const p = new URLSearchParams(params)
    if (next > 0) p.set('offset', String(next))
    else p.delete('offset')
    setParams(p)
  }

  async function sendNow(id: number) {
    setSendingId(id)
    setError(null)
    try {
      await admin.emailSendNow(id)
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setSendingId(null)
    }
  }

  const from = total === 0 ? 0 : offset + 1
  const to = Math.min(offset + PAGE_SIZE, total)

  return (
    <section>
      <div className="page-header">
        <h1>Emails</h1>
        <p className="text-sm text-text-muted">{total} email{total === 1 ? '' : 's'}</p>
      </div>

      {error && <div className="banner-err mb-md">{error}</div>}
      {throttle && <ThrottleBanner throttle={throttle} />}

      <div className="filter-bar">
        <label className="flex items-center gap-sm">
          Estado:
          <select
            className="input w-auto"
            value={status}
            onChange={(e) => setParam('status', e.target.value)}
          >
            <option value="">todos</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-sm">
          Kind:
          <input
            type="text"
            className="input w-auto"
            placeholder="ej: questionnaire_invitation"
            value={kind}
            onChange={(e) => setParam('kind', e.target.value)}
          />
        </label>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Destinatario</th>
              <th>Kind</th>
              <th>Asunto</th>
              <th>Estado</th>
              <th>Sale / salió</th>
              <th className="text-right">Intentos</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items === null && (
              <tr>
                <td colSpan={7} className="text-text-muted">
                  Cargando…
                </td>
              </tr>
            )}
            {items?.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-text-muted">
                  Sin emails con esos filtros.
                </td>
              </tr>
            )}
            {items?.map((e) => {
              const expanded = expandedId === e.id
              const pending = e.status === 'queued' || e.status === 'failed'
              return (
                <Fragment key={e.id}>
                  <tr
                    className={e.last_error ? 'cursor-pointer' : undefined}
                    onClick={() => e.last_error && setExpandedId(expanded ? null : e.id)}
                    title={e.last_error ?? undefined}
                  >
                    <td>
                      {e.recipient}
                      {e.override_applied && (
                        <span className="tag ml-sm" title="No le llegó al destinatario real: salió re-ruteado al override">
                          re-ruteado
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="tag normal-case tracking-normal">{e.kind}</span>
                    </td>
                    <td className="max-w-[280px] truncate text-text-secondary">{e.subject}</td>
                    <td>
                      <EmailStatusBadge row={e} />
                    </td>
                    <td className="text-text-muted">
                      <EmailWhen row={e} throttle={throttle} />
                    </td>
                    <td className="text-right">{e.attempts}</td>
                    <td className="text-right">
                      {pending && (
                        <button
                          type="button"
                          className="btn-ghost text-xs"
                          disabled={sendingId !== null || !throttle?.email_configured}
                          title={
                            throttle?.email_configured
                              ? 'Manda este mail ahora, salteando el freno global'
                              : 'No hay transporte de mail configurado'
                          }
                          onClick={(ev) => {
                            ev.stopPropagation()
                            void sendNow(e.id)
                          }}
                        >
                          {sendingId === e.id ? 'Enviando…' : 'Enviar ahora'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded && e.last_error && (
                    <tr>
                      <td colSpan={7} className="bg-danger/5 text-xs text-danger">
                        {e.last_error}
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="mt-md flex items-center justify-between text-sm text-text-muted">
          <span>
            {from}–{to} de {total}
          </span>
          <div className="flex gap-sm">
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              ← Anterior
            </button>
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

const STATUS_LABEL: Record<AdminEmailStatus, string> = {
  queued: 'En cola',
  sent: 'Enviado',
  failed: 'Fallido',
  skipped: 'Descartado',
}

export function formatClock(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatStamp(iso: string): string {
  return new Date(iso).toLocaleString('es-AR', {
    dateStyle: 'short',
    timeStyle: 'short',
  })
}

/** Explica el freno arriba de la tabla: sin esto, "5 en cola" no dice nada
 *  sobre cuándo van a salir, que es justo lo que importa con un mail cada
 *  media hora. */
export function ThrottleBanner({ throttle }: { throttle: AdminEmailThrottle }) {
  if (!throttle.email_configured) {
    return (
      <div className="banner-err mb-md">
        No hay transporte de mail configurado: nada sale. La cola se sigue
        llenando y se manda cuando aterrice la config.
      </div>
    )
  }
  if (!throttle.sending_enabled) {
    return (
      <div className="banner-warn mb-md">
        Envío APAGADO (<code>EMAIL_SENDING_ENABLED=false</code>). Se sigue
        encolando todo; no se pierde nada y sale ordenado cuando se prenda.
      </div>
    )
  }

  const minutes = Math.round(throttle.min_interval_s / 60)
  return (
    <div className="banner-info mb-md text-sm">
      {throttle.min_interval_s > 0 ? (
        <>
          Freno global: <strong>1 mail cada {minutes} min</strong>.{' '}
          {throttle.next_send_at ? (
            <>El próximo sale a las {formatClock(throttle.next_send_at)}.</>
          ) : (
            <>No hay nada en cola.</>
          )}
        </>
      ) : (
        <>Freno global apagado: la cola sale sin espera entre mails.</>
      )}
      {throttle.override_recipient && (
        <>
          {' '}
          <strong>
            Ojo: todo el correo se re-rutea a {throttle.override_recipient}
          </strong>{' '}
          — a los destinatarios reales no les llega nada.
        </>
      )}
    </div>
  )
}

/** Cuándo salió (si salió) o cuándo sale. Para los pendientes usa el
 *  `next_send_at` del freno cuando el mail es el primero de la cola: su
 *  `scheduled_for` puede estar vencido hace rato y aun así no haber salido. */
export function EmailWhen({
  row,
  throttle,
}: {
  row: AdminEmailRow
  throttle: AdminEmailThrottle | null
}) {
  if (row.sent_at) {
    return (
      <>
        {formatStamp(row.sent_at)}
        {row.forced_at && (
          <span className="tag ml-sm" title="Enviado a mano salteando el freno">
            forzado
          </span>
        )}
      </>
    )
  }
  if (row.status === 'failed') return <>—</>
  if (!row.scheduled_for) return <>—</>

  const scheduled = new Date(row.scheduled_for).getTime()
  const slot = throttle?.next_slot_at ? new Date(throttle.next_slot_at).getTime() : 0
  const eta = new Date(Math.max(scheduled, slot)).toISOString()
  return <>sale a las {formatClock(eta)}</>
}

/** Estado visual. Ya no se deriva de sent_at/last_error: el backend manda
 *  `status` explícito, porque un reintento con backoff (last_error seteado,
 *  todavía en cola) NO es un fallo. */
export function EmailStatusBadge({ row }: { row: AdminEmailRow }) {
  if (row.status === 'sent') return <span className="tag-ok">Enviado</span>
  if (row.status === 'failed') return <span className="tag-err">Fallido</span>
  if (row.status === 'skipped') return <span className="tag">Descartado</span>
  if (row.last_error)
    return (
      <span className="tag-warn" title={row.last_error}>
        Reintentando
      </span>
    )
  return <span className="tag">En cola</span>
}
